'use client'

import Image from 'next/image'

import { clsx } from 'clsx'
import Icon from '@/components/svg/icon'
import type {
	ProductFragment,
	ProductSubCategoryFragment,
} from '@/lib/types/datocms/graphql/generated'

type Props = {
	subCategory: ProductSubCategoryFragment
	handleAccordionClick: (id: string) => void
	activeAccordion: string
	setActiveProduct: (id: string | null) => void
	activeProduct: string | null
	products: ProductFragment[]
}

export default function CategorySubMenu({
	subCategory,
	handleAccordionClick,
	activeAccordion,
	setActiveProduct,
	activeProduct,
	products,
}: Readonly<Props>) {
	const handleProductClick = (id: string | null) => {
		setActiveProduct(id)
	}
	// Ensure unique products
	const uniqueProducts = Array.from(
		new Map(products.map((product) => [product.id, product])).values()
	)

	return (
		<div key={subCategory.id as string} className="relative z-10">
			{/* Accordion Header */}
			{uniqueProducts?.length > 0 && (
				<button
					className="flex justify-between items-center text-lg text-left font-semibold w-full px-3 lg:px-8 py-5 border-b bg-white-frosted hover:bg-white-frosted-shade transition-colors duration-300"
					onClick={() => handleAccordionClick(subCategory.id as string)}
				>
					{subCategory.title}
					<Icon
						icon="Chevron"
						className={clsx(
							'w-6 h-4 transition-transform duration-300 text-blue',
							activeAccordion === subCategory.id ? '-rotate-90' : 'rotate-90'
						)}
					/>
				</button>
			)}

			{/* Accordion Content */}
			<div
				className={clsx(
					'grid overflow-hidden transition-all duration-300 ease-out',
					activeAccordion === subCategory.id && uniqueProducts?.length > 0
						? 'grid-rows-[1fr] border-b'
						: 'grid-rows-[0fr]'
				)}
			>
				<div className="overflow-hidden">
					{uniqueProducts?.map((item) => (
						<button
							key={item.id}
							onClick={() => handleProductClick(item.id)}
							className={clsx(
								'relative flex items-center gap-4 text-left w-full px-3 py-2',
								activeProduct === item.id ? 'bg-white' : 'bg-white-frosted',
								'before:content-[""] before:absolute before:inset-y-0 before:left-0 before:w-1 before:transition-opacity before:duration-300',
								activeProduct === item.id ? 'before:bg-blue' : 'before:bg-transparent'
							)}
						>
							{/* Product Image */}
							<div className="w-[90px] h-[70px] shrink-0 bg-gray-light/50 rounded-md overflow-hidden">
								{item.gallery?.[0] && (
									<Image
										src={item.coverImage?.[0]?.url as string}
										alt={item.coverImage?.[0]?.alt ?? ''}
										width={50}
										height={50}
										className="object-cover w-full h-full"
										sizes="90px"
									/>
								)}
							</div>
							{/* Product Info */}
							<div className="flex flex-col">
								<span className="font-semibold leading-tight">{item.name}</span>
								<span className="text-sm">{item.description}</span>
							</div>
						</button>
					))}
				</div>
			</div>
		</div>
	)
}
